import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { fileNameFor } from "./exportElectricalPdf";
import { fetchPhotos, photoReport, assetBytes } from "./photos";
import logoArt from "../../../momentum_logo.png";

/**
 * The findings register as a spreadsheet. Ported from
 * Csa/report/exportExcel.js.
 *
 * Excel always produces the register, in both layouts. Elaborate is a
 * print-layout idea — one entry per finding, set out down the page — and it
 * does not translate to a spreadsheet. What a client wants from Excel is to
 * filter and sort the findings themselves, which is exactly the register.
 *
 * One column narrower than CSA's: Electrical's checklist carries no
 * verification_method field.
 */

const C = {
  ink: "FF1E2A28",
  tealDark: "FF245952",
  white: "FFFFFFFF",
  sunk: "FFF1F4F3",
  gold: "FFEFC71D",
  yes: "FF2E7D4F",
  partial: "FFB8860B",
  no: "FFC0392B",
  na: "FF7C8A87",
};

const statusFill = (s) => {
  const v = String(s || "").toUpperCase();
  if (v === "YES") return C.yes;
  if (v === "PARTIAL") return C.partial;
  if (v === "NO") return C.no;
  return C.na;
};

/**
 * Excel column widths are in characters; the model's are percentages of a
 * printed page. Multiplying gets close enough, with a floor so the narrow
 * columns stay readable and a ceiling so Observation does not run off screen.
 */
const widthFor = (col) => Math.min(60, Math.max(8, Math.round(col.width * 1.9)));

function styleHeader(row) {
  row.font = { bold: true, size: 9, color: { argb: C.white } };
  row.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.tealDark } };
  row.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  row.height = 26;
}

/* ------------------------------------------------------------------ */

function summarySheet(wb, model, mark) {
  const ws = wb.addWorksheet("Summary", {
    properties: { defaultColWidth: 22 },
    views: [{ showGridLines: false }],
  });
  stampLogo(ws, mark);

  const h = model.header;
  const s = model.summary;

  ws.addRow(["Electrical Audit"]).font = {
    bold: true, size: 16, color: { argb: C.tealDark },
  };
  ws.addRow([]);

  const pairs = [
    ["Client", h.organization],
    ["Site", h.site],
    ["Assessment dates", [h.start_date, h.end_date].filter(Boolean).join(" to ") || "—"],
    ["Framework version", h.framework_version],
    ["Report reference", h.report_id],
    ...(h.issue
      ? [
          ["Issue", `${h.issue.no}  ·  ${new Date(h.issue.issued_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`],
          ["This issue reports", `${h.issue.selected_count} of ${h.issue.assessed_count} assessed findings`],
        ]
      : []),
    ["Prepared by", h.prepared_by || "—"],
    ["Reviewed by", h.was_reviewed ? h.reviewed_by || "Reviewed" : "Not reviewed"],
    [],
    ["Overall conformance", s.overall_pct === null ? "Not assessed" : s.overall_pct / 100],
    ["Maturity band", s.maturity_band || "—"],
    [],
    ["Checkpoints assessed", s.counts.assessed],
    ["Conformant", s.counts.yes],
    ["Partially conformant", s.counts.partial],
    ["Non-conformant", s.counts.no],
    ["Not applicable", s.counts.na],
    ["Findings in this report", s.selected],
  ];

  pairs.forEach((p) => {
    const row = ws.addRow(p);
    if (p.length) {
      row.getCell(1).font = { bold: true, size: 10 };
      row.getCell(2).alignment = { horizontal: "left" };

      if (p[0] === "Overall conformance" && typeof p[1] === "number") {
        row.getCell(2).numFmt = "0.0%";
      }
    }
  });

  ws.getColumn(1).width = 26;
  ws.getColumn(2).width = 46;

  return ws;
}

const PHOTO_PX = { width: 96, height: 72 };
const PHOTO_ROW_HEIGHT = 58;
const PHOTO_COL_WIDTH = 16;
const PHOTO_COL_LABEL = "Photo";

const LOGO_PX = { width: 200, height: 51 };
const LOGO_ROW_HEIGHT = 42;

/** Put the wordmark at the top of a sheet. */
function stampLogo(ws, id) {
  if (id === null || id === undefined) return 0;
  try {
    ws.addRow([]);
    ws.getRow(1).height = LOGO_ROW_HEIGHT;
    ws.addImage(id, { tl: { col: 0.15, row: 0.15 }, ext: LOGO_PX, editAs: "oneCell" });
    return 1;
  } catch (e) {
    return 0;
  }
}

function findingsSheet(wb, model, mark) {
  const data = model.sections.find((x) => x.key === "findings").data;
  const cols = data.columns;

  const ws = wb.addWorksheet("Findings");
  const top = stampLogo(ws, mark);
  ws.views = [{ state: "frozen", ySplit: top + 1 }];

  ws.columns = [
    ...cols.map((c) => ({ key: c.key, width: widthFor(c) })),
    { key: "photo_image", width: PHOTO_COL_WIDTH },
  ];
  styleHeader(ws.addRow([...cols.map((c) => c.label), PHOTO_COL_LABEL]));

  data.rows.forEach((r) => {
    const row = ws.addRow([
      r.sr_no,
      r.area,
      r.category,
      r.status,
      r.observation,
      r.recommendation || "",
      r.legal_reference || "",
      r.score === null ? "" : r.score,
      (r.photos || []).join("\n"),
    ]);

    row.alignment = { vertical: "top", wrapText: true };
    row.getCell(4).font = { bold: true, color: { argb: C.white } };
    row.getCell(4).fill = {
      type: "pattern", pattern: "solid", fgColor: { argb: statusFill(r.status) },
    };
    row.getCell(4).alignment = { vertical: "top", horizontal: "center" };
    row.getCell(1).alignment = { vertical: "top", horizontal: "center" };
    row.getCell(8).alignment = { vertical: "top", horizontal: "center" };
  });

  if (data.rows.length) {
    ws.autoFilter = {
      from: { row: top + 1, column: 1 },
      to: { row: top + 1, column: cols.length },
    };
  }

  return ws;
}

/**
 * Put the photographs in, once the workbook is otherwise finished. Never
 * throws: a picture that will not embed leaves that row with its URL.
 */
export function insertPhotos(wb, images) {
  if (!wb || !images || !images.size) return 0;

  const ws = wb.getWorksheet("Findings");
  if (!ws) return 0;

  let headerRow = 0;
  let urlCol = null;
  let photoCol = null;
  for (let n = 1; n <= Math.min(5, ws.rowCount); n++) {
    const vals = ws.getRow(n).values;
    let u = null;
    let p = null;
    vals.forEach((v, i) => {
      if (v === "Evidence" || v === "Objective Evidence") u = i;
      if (v === PHOTO_COL_LABEL) p = i;
    });
    if (u && p) { headerRow = n; urlCol = u; photoCol = p; break; }
  }
  if (!headerRow) return 0;

  let placed = 0;
  for (let n = headerRow + 1; n <= ws.rowCount; n++) {
    const cell = ws.getRow(n).getCell(urlCol).value;
    const urls = String(cell && cell.text ? cell.text : cell || "")
      .split("\n").map((u) => u.trim()).filter(Boolean);
    const shot = urls.find((u) => images.get(u));
    if (!shot) continue;

    try {
      const id = wb.addImage({ buffer: images.get(shot), extension: "jpeg" });
      ws.getRow(n).height = PHOTO_ROW_HEIGHT;
      ws.addImage(id, {
        tl: { col: photoCol - 1 + 0.1, row: n - 1 + 0.1 },
        ext: PHOTO_PX,
        editAs: "oneCell",
      });
      placed++;
    } catch (e) {
      /* this row keeps its URL and loses its picture; the rest carry on */
    }
  }

  return placed;
}

function scoresSheet(wb, model, mark) {
  const d = model.sections.find((x) => x.key === "score_analysis").data;
  const ws = wb.addWorksheet("Scores");
  const top = stampLogo(ws, mark);
  ws.views = [{ state: "frozen", ySplit: top + 1 }];

  ws.columns = [
    { width: 8 }, { width: 34 }, { width: 40 }, { width: 12 }, { width: 10 }, { width: 14 },
  ];
  styleHeader(ws.addRow(["Area", "Area name", "Category", "Assessed", "Score", "Band"]));

  d.areas.forEach((a) => {
    const head = ws.addRow([a.no, a.name, "— area total —", `${a.assessed}/${a.total}`,
      a.pct === null ? "" : a.pct / 100, a.band]);
    head.font = { bold: true };
    head.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.sunk } };
    head.getCell(5).numFmt = "0.0%";

    a.categories.forEach((c) => {
      const row = ws.addRow(["", "", c.name, `${c.assessed}/${c.total}`,
        c.pct === null ? "" : c.pct / 100, c.band]);
      row.getCell(5).numFmt = "0.0%";
    });
  });

  return ws;
}

function scopeSheet(wb, model, mark) {
  const d = model.sections.find((x) => x.key === "scope").data;
  if (!d.exclusions.length) return null;

  const ws = wb.addWorksheet("Scope");
  const top = stampLogo(ws, mark);
  ws.views = [{ state: "frozen", ySplit: top + 1 }];
  ws.columns = [{ width: 34 }, { width: 12 }, { width: 80 }];
  styleHeader(ws.addRow(["Excluded", "Level", "Reason"]));

  d.exclusions.forEach((e) => {
    const row = ws.addRow([e.name, e.level === "category" ? "Category" : "Area", e.reason]);
    row.alignment = { vertical: "top", wrapText: true };
  });

  return ws;
}

/* ------------------------------------------------------------------ */

/** Build the workbook. Separated from saving so it can be tested. */
export async function buildWorkbook(model, logo) {
  const wb = new ExcelJS.Workbook();

  const mark = logo ? wb.addImage({ buffer: logo, extension: "png" }) : null;
  wb.creator = model.header.prepared_by || "Momentum";
  wb.created = new Date();
  wb.title = `Electrical — ${model.header.organization} — ${model.header.site}`;

  summarySheet(wb, model, mark);
  findingsSheet(wb, model, mark);
  scoresSheet(wb, model, mark);
  scopeSheet(wb, model, mark);

  return wb;
}

export async function exportReportExcel(model, opts = {}) {
  const wb = await buildWorkbook(model, await assetBytes(logoArt));
  const images = await fetchPhotos(model, opts.photoBytes);
  insertPhotos(wb, images);
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const name = opts.fileName || fileNameFor(model, "xlsx");
  saveAs(blob, name);
  return { name, bytes: blob.size, photos: photoReport(images) };
}
